import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CognitiveSearch } from "./CognitiveSearch";
import { useI18n } from "../i18n/LanguageContext";

const HERO_BG =
  "https://images.unsplash.com/photo-1770520218879-8c9c3fe9f681";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero-search"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_70%_10%,#12335e_0%,#0A1A2F_45%,#05101f_100%)]" />
      {/* Parallax image */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0"
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-[0.18] [mask-image:radial-gradient(70%_60%_at_60%_40%,#000_0%,transparent_75%)]"
        />
      </motion.div>
      <div className="absolute inset-0 grid-lines opacity-40" />

      {/* Floating geometric accents */}
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], ["0%", "120%"]) }}
        className="absolute top-32 right-[12%] w-32 h-32 rounded-full border border-[#4BE3FF]/30 animate-float-slow hidden md:block"
      />
      <div className="absolute bottom-40 left-[8%] w-40 h-40 border border-[#1E6FFF]/20 rotate-45 animate-spin-slow hidden lg:block" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 w-full max-w-6xl mx-auto px-6"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="font-mono text-[13px] tracking-[0.35em] uppercase text-[#4BE3FF] mb-6 flex items-center gap-3"
        >
          <span className="inline-block w-8 h-px bg-[#4BE3FF]" />
          {t("hero.kicker")}
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight text-[clamp(2.8rem,9vw,7rem)] leading-[0.95] text-white">
          <span className="reveal-mask">
            <motion.span
              custom={0}
              variants={lineVariant}
              initial="hidden"
              animate="show"
              className="block"
            >
              {t("hero.titleLine1")}
            </motion.span>
          </span>
          <span className="reveal-mask">
            <motion.span
              custom={1}
              variants={lineVariant}
              initial="hidden"
              animate="show"
              className="block"
            >
              {t("hero.titleLine2Prefix")}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4BE3FF] to-[#1E6FFF] text-glow">
                {t("hero.titleLine2Highlight")}
              </span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-[#A7B4C2] font-ui"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10"
        >
          <CognitiveSearch />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-6 flex flex-wrap items-center gap-2 pl-1"
        >
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#A7B4C2]/60">
            {t("hero.try")}
          </span>
          {t("suggestions").map((s) => (
            <span
              key={s}
              className="font-mono text-[12px] text-[#A7B4C2] border border-white/10 rounded-full px-3 py-1 hover:border-[#4BE3FF]/50 hover:text-[#4BE3FF] transition-colors duration-300 cursor-default truncate max-w-[220px]"
            >
              {s}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
